import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import URLS from 'constants/urls';
import { itemPropType } from 'constants/propTypes';
import { transformV2ClientResponse } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { selectActiveVenue } from 'selectors/general';

import { FOOD_AND_BEVERAGE_TAG_NAME } from '../Items.constants';
import EditItemForm from './EditItemForm';
import AddItemForm from './AddItemForm';

export function getDefaultAvailability(items) {
  const availabilitiesOccurrences = items.reduce((dict, item) => {
    const stringified = JSON.stringify(item.availabilityRecurrence);
    const { value: currentValue, smallestItemId: currentSmallestItemId } = dict.has(stringified)
      ? dict.get(stringified)
      : { value: 0, smallestItemId: Infinity };

    const newValue = currentValue + 1;
    const newSmallestItemId = item.id < currentSmallestItemId ? item.id : currentSmallestItemId;

    dict.set(stringified, { value: newValue, smallestItemId: newSmallestItemId });

    return dict;
  }, new Map());

  if (availabilitiesOccurrences.size > 0) {
    const highestOccurrence = Math.max(...Array.from(availabilitiesOccurrences.values(), ({ value }) => value));
    const [mostFrequentEarliestOccurrenceStringified] = Array.from(availabilitiesOccurrences.entries()).reduce(
      (previous, current) => {
        const [, { value: previousValue, smallestItemId: previousSmallestItemId }] = previous;
        const [, { value, smallestItemId }] = current;

        const isCurrentOfHighestOccurrence = value === highestOccurrence;
        const isPreviousOfHighestOccurrence = previousValue;
        const isCurrentSmallestIdSmallerThanPreviousSmallestId = smallestItemId < previousSmallestItemId;

        if (
          isCurrentOfHighestOccurrence &&
          (!isPreviousOfHighestOccurrence || isCurrentSmallestIdSmallerThanPreviousSmallestId)
        ) {
          return current;
        }

        return previous;
      }
    );

    return JSON.parse(mostFrequentEarliestOccurrenceStringified);
  }

  return null;
}

const ItemForm = ({ items, onSuccess }) => {
  const { currencyCode, venueId } = useSelector(selectActiveVenue);

  const [isLoading, setIsLoading] = useState(null);
  const [foodAndBeverageL1Tag, setFoodAndBeverageL1Tag] = useState(null);
  const [categoryTags, setCategoryTags] = useState([]);

  const existingItemNames = items.map(({ name }) => name.toLowerCase());

  const fetchClassTags = async () => {
    setIsLoading(true);
    try {
      const classTags = transformV2ClientResponse(await httpClient.get(URLS.class.tags({ excludeOutdoors: true })));

      const { id: foodAndBeverageL1Id, name: foodAndBeverageL1Name } = classTags[FOOD_AND_BEVERAGE_TAG_NAME];
      setFoodAndBeverageL1Tag({ id: foodAndBeverageL1Id, name: foodAndBeverageL1Name });

      const foodAndBeverageTags = classTags[FOOD_AND_BEVERAGE_TAG_NAME].subtags.map(({ id, name }) => {
        return {
          id,
          name,
        };
      });
      setCategoryTags(foodAndBeverageTags);
    } catch (error) {
      reportError(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchClassTags();
  }, []);

  const itemFormProps = {
    isLoading,
    setIsLoading,
    currencyCode,
    existingItemNames,
    categoryTags,
    foodAndBeverageL1Tag,
    onSuccess,
    venueId,
  };

  const defaultAvailability = getDefaultAvailability(items);

  return (
    <>
      <AddItemForm {...itemFormProps} defaultAvailability={defaultAvailability} />
      <EditItemForm {...itemFormProps} items={items} />
    </>
  );
};

ItemForm.propTypes = {
  items: PropTypes.arrayOf(itemPropType),
  onSuccess: PropTypes.func,
};

export default ItemForm;
